import { Injectable } from '@angular/core';
import { CanActivate, Router } from '@angular/router';
import { UserService } from '../services/user.service';
import { MsalService } from '@azure/msal-angular';
import { firstValueFrom, timeout, filter } from 'rxjs';
import { SessionService } from '../services/session.service';
import { LoginService } from '../services/login.service';

@Injectable({
	providedIn: 'root'
})
export class AuthGuard implements CanActivate {
	private initializationPromise: Promise<void> | null = null;

	constructor(
		private userService: UserService,
		private router: Router,
		private msalService: MsalService,
		private sessionService: SessionService,
		private loginService: LoginService
	) { }

	async canActivate(): Promise<boolean> {
		try {
			if (this.userService.isLoggedIn) {
				return true;
			}

			const hasValidSession = await this.checkNormalLoginSession();
			if (hasValidSession) {
				return true;
			}

			const hasSsoSession = await this.checkSsoLoginSession();
			if (hasSsoSession) {
				return true;
			}

			this.router.navigate(['/']);
			return false;

		} catch (error) {
			this.router.navigate(['/']);
			return false;
		}
	}

	private async checkNormalLoginSession(): Promise<boolean> {
		try {
			const accessToken = this.sessionService.getAccessToken();

			if (!accessToken || this.sessionService.isTokenExpired()) {
				return false;
			}

			if (!this.userService.isLoggedIn) {
				const userClaims = await firstValueFrom(this.loginService.getInfo(accessToken));
				if (userClaims) {
					this.userService.setUserInfo(userClaims);
					return true;
				}
			}
			return this.userService.isLoggedIn;
		} catch (error) {
			this.sessionService.clearSession();
			return false;
		}
	}

	private async checkSsoLoginSession(): Promise<boolean> {
		try {
			if (!this.initializationPromise) {
				this.initializationPromise = this.initializeMsal();
			}
			await this.initializationPromise;

			const activeAccount = this.msalService.instance.getActiveAccount();

			if (activeAccount) {
				try {
					await firstValueFrom(
						this.userService.userInfo$.pipe(
							filter(userInfo => userInfo !== null),
							timeout(5000)
						)
					);
					return this.userService.isLoggedIn;
				} catch (error) {
					return true;
				}
			}
			return false;
		} catch (error) {
			return false;
		}
	}

	private async initializeMsal(): Promise<void> {
		try {
			await this.msalService.initialize();
			
			await new Promise(resolve => setTimeout(resolve, 100));
		} catch (error) {
			throw error;
		}
	}
}
